import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// Helpers
import { and } from "@/Common/helpers/sonar";
import { getSections, getSectionsAvailable } from "@PayPlans/helpers/sections";
import { getMappedOEMFranchises, getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";

// Hooks
import { useLocationFS } from "@/Common/hooks/LocationFS";
import { getExtendedBranchesFranchises, getOEMFranchisePrograms } from "@/Common/hooks/LocationPrograms";

// Defaults builders
import {
	CallActivityPerformanceDefault,
	DefaultGrossDepartmental,
	CustomerSatisfactionDefault,
	LongevityDefault,
	ExcellenceMakesDefault,
	OperatingCostsDefault,
} from "@PayPlans/helpers/DefaultSections";

// Contexts
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Layouts
import PayPlansLayout from "@/PayPlans/Layouts/PayPlansLayout";
import SectionContainer from "@Layouts/components/SectionContainer";

// Custom Sections
import {
	CallActivityPerformance,
	CustomerSatisfaction,
	EBEBonus,
	Longevity,
	GrossDepartmental,
	PASEBonus,
	OperatingCosts,
} from "./sections";

// MUI
import { Collapse, Grid } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import PdfPayPlanLayer from "@/PayPlans/views/Layers/Html/Sales/Associates";

const allSections = getSections([
	"bodyShop",
	"parts",
	"service",
	"customerSatisfaction",
	"callActivityPerformance",
	"ebe",
	"pase",
	"longevity",
	"operatingCosts",
]);

// Used to build franchises dependant sections
const excludesMakes = ["Cadillac"];
const includesMakes = ["Chevrolet", "Buick", "GMC"];
const checkPrograms = ["ebe", "pase"];

// Constants
const contentAlignment = "flex-start";
const DEFAULT_SECTION = "Default Commissions";

const OPTIONAL_SECTIONS = new Map([
	["bodyShop", DefaultGrossDepartmental],
	["parts", DefaultGrossDepartmental],
	["service", DefaultGrossDepartmental],
	["callActivityPerformance", CallActivityPerformanceDefault],
	["customerSatisfaction", CustomerSatisfactionDefault],
	["ebe", ExcellenceMakesDefault],
	["pase", ExcellenceMakesDefault],
	["longevity", LongevityDefault],
	["operatingCosts", OperatingCostsDefault],
]);

const getSectionsToRemove = (sections = []) => checkPrograms.filter((program) => !sections?.find((item) => item?.program === program));
const getIsSectionButtonDisabled = ({ isValid = false, isReadOnlyReason = false, isSomeNewPayPlan = false, loading = false }) =>
	!isValid || isReadOnlyReason || isSomeNewPayPlan || loading;

export default function PayPlansModel(props) {
	const { data = null, location = null, isNew = false, loading = false, readOnly = false, updateModel = () => {} } = props;

	// Hooks
	const { isSomeNewPayPlan, isValid, selected, useSectionsErrors, position } = usePayPlan();
	const { errors, clearErrors, clearSectionErrors } = useSectionsErrors();
	const { data: locationFS, isLoading: loadingFS } = useLocationFS({ locationId: location.locationId });

	const isReadOnly = !loading && (Boolean(data?.available) || position !== data?.position);
	const locationOEMs = getSanitatedLocationOEMs(location?.oems);

	// States
	const [isLazyLoading, setIsLazyLoading] = useState(true);
	const [sections, setSections] = useState(new Map());

	const isDefaultsDeletable = (innerData = {}) => {
		const { bodyShop, parts, service } = innerData;
		const presence = [bodyShop, parts, service].filter((item) => item);
		return new Set(presence).size > 1;
	};

	// Programs usage
	const programsUsage = useMemo(() => {
		const extendedBranchesFranchises = getExtendedBranchesFranchises(location?.branchesOEMs, location?.branchesFranchises);
		return getOEMFranchisePrograms(locationFS?.oems, extendedBranchesFranchises);
	}, [location, locationFS]);

	// Sections to remove
	const removeSections = useMemo(() => getSectionsToRemove(programsUsage), [programsUsage]);

	// Sections to disable
	const disableSections = programsUsage?.filter((item) => item.disabled).map((item) => item.program) || [];

	// Sections controls
	const handleAddSection = (key) => {
		const sectionFn = OPTIONAL_SECTIONS.get(key);
		const isDefault = ["bodyShop", "parts", "service"].includes(key);
		const isEBEorPASE = ["ebe", "pase"].includes(key);
		const isRequiredSectionParams = ["customerSatisfaction", "longevity", "operatingCosts"].includes(key);
		const mappedOEMFranchises = getMappedOEMFranchises(locationOEMs ?? [], location?.franchises ?? []) ?? [];
		if (isDefault) {
			data[key] = sectionFn(locationOEMs, key);
		} else if (isEBEorPASE) {
			data[key] = sectionFn(mappedOEMFranchises, includesMakes);
		} else if (isRequiredSectionParams) {
			switch (key) {
				case "customerSatisfaction":
					const mappedOEMBranchesFranchises = getMappedOEMFranchises(
						location?.branchesOEMs ?? [],
						location?.branchesFranchises ?? []
					);
					data[key] = sectionFn(mappedOEMBranchesFranchises ?? [], excludesMakes, { useOnlyStoreMetrics: true });
					break;
				case "longevity":
					data[key] = sectionFn(true);
					break;
				case "operatingCosts":
					data[key] = sectionFn(locationOEMs);
					break;
				default:
					break;
			}
		} else {
			data[key] = sectionFn();
		}

		setSections(getSectionsAvailable(allSections, data, removeSections));
	};

	const handleDeleteSection = (section) => {
		const remove = allSections.get(section);

		// Remove section
		data[section] = null;
		if (remove?.type === "list") {
			data[section] = [];
		}

		clearSectionErrors(section);
		setSections(getSectionsAvailable(allSections, data, removeSections));
		updateModel({ section, data: data[section] });
	};

	const isACommonUpdateSection = (section) => {
		const commons = ["bodyShop", "parts", "service", "callActivityPerformance", "longevity", "operatingCosts", "ebe", "pase"];
		return commons.includes(section);
	};

	const handleUpdateSection = (section, update = "", value = null, isValidUpdate = true) => {
		const isCommonUpdatable = isACommonUpdateSection(section);

		if (isCommonUpdatable) {
			data[section] = value;
		} else if (section === "customerSatisfaction") {
			if (update === "weighting") {
				data[section][update] = value;
			} else {
				data[section]["makeMetrics"]?.forEach((makeMetric, key) => {
					if (makeMetric.make === update) {
						data[section]["makeMetrics"][key] = value;
					}
				});
			}
		}

		// Update model
		Boolean(isValidUpdate) && updateModel({ section, data: data[section] });
	};

	useMemo(() => {
		setSections(getSectionsAvailable(allSections, data, removeSections));
	}, [data, removeSections]);

	const isDOMLoading = useMemo(() => Boolean(isLazyLoading || loading || loadingFS), [isLazyLoading, loading, loadingFS]);

	useMemo(() => {
		const timer = 500;
		setIsLazyLoading(true);
		if (selected) {
			setTimeout(() => {
				setIsLazyLoading(false);
			}, timer);
		}
	}, [selected, setIsLazyLoading]);

	useEffect(() => {
		return () => {
			clearErrors();
		};
	}, [clearErrors]);

	const isReadOnlyReason = readOnly || isReadOnly;

	return (
		<PayPlansLayout
			htmlComponent={<PdfPayPlanLayer payPlan={data} selected={selected} loading={loading} />}
			data={data}
			allSections={allSections}
			sections={sections}
			disabledSections={disableSections}
			disabled={getIsSectionButtonDisabled({
				isValid,
				isReadOnlyReason,
				isSomeNewPayPlan,
				loading,
			})}
			isNew={isNew}
			isValid={isValid}
			loading={loading}
			readOnly={isReadOnlyReason}
			onActionAddSection={handleAddSection}
		>
			<TransitionGroup component={Grid} container item xs={12}>
				{Boolean(isDOMLoading) && (
					<Collapse sx={{ width: 1 }}>
						<SectionContainer sectionId={"default"} title={DEFAULT_SECTION} collapsible loading={true} readOnly={true}>
							<GrossDepartmental
								sectionId={"default"}
								content={{ makes: [], combineOEMs: false, isGuaranteedDraw: false }}
								useSectionsErrors={useSectionsErrors}
								loading={true}
								readOnly={true}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("bodyShop")), !isDOMLoading) && (
					<Collapse sx={{ width: 1 }}>
						<SectionContainer
							sectionId={"bodyShop"}
							title={DEFAULT_SECTION}
							collapsible
							deletable={isDefaultsDeletable(data)}
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<GrossDepartmental
								sectionId={"bodyShop"}
								content={data?.bodyShop}
								locationOEMs={locationOEMs}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("parts")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: data?.bodyShop ? 2 : 0 }}>
						<SectionContainer
							sectionId={"parts"}
							title={DEFAULT_SECTION}
							collapsible
							deletable={isDefaultsDeletable(data)}
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<GrossDepartmental
								sectionId={"parts"}
								content={data?.parts}
								locationOEMs={locationOEMs}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("service")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: data?.bodyShop || data?.parts ? 2 : 0 }}>
						<SectionContainer
							sectionId={"service"}
							title={DEFAULT_SECTION}
							collapsible
							deletable={isDefaultsDeletable(data)}
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<GrossDepartmental
								sectionId={"service"}
								content={data?.service}
								locationOEMs={locationOEMs}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("callActivityPerformance")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"callActivityPerformance"}
							title={sections.get("callActivityPerformance").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<CallActivityPerformance
								sectionId={"callActivityPerformance"}
								performance={data?.callActivityPerformance || null}
								loading={loading}
								readOnly={isReadOnlyReason}
								paySwitchIsEditable={true}
								useSectionsErrors={useSectionsErrors}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("operatingCosts")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"operatingCosts"}
							title={sections.get("operatingCosts").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<OperatingCosts
								sectionId={"operatingCosts"}
								content={data?.operatingCosts}
								oems={locationOEMs ?? []}
								loading={loading}
								readOnly={isReadOnlyReason}
								useSectionsErrors={useSectionsErrors}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}
			</TransitionGroup>

			<Grid container item xs={12} columnSpacing={2} sx={!loading ? { paddingTop: "0px !important" } : null}>
				{!isDOMLoading && (
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("customerSatisfaction") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"customerSatisfaction"}
									title={sections.get("customerSatisfaction").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<CustomerSatisfaction
										sectionId={"customerSatisfaction"}
										weightLabel={"gross value"}
										content={data?.customerSatisfaction}
										errors={errors?.get("customerSatisfaction")}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("ebe") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"ebe"}
									title={sections.get("ebe").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<EBEBonus
										sectionId={"ebe"}
										reference={"EBE"}
										content={data?.ebe}
										loading={loading}
										readOnly={isReadOnlyReason}
										useSectionsErrors={useSectionsErrors}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("longevity") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"longevity"}
									title={sections.get("longevity").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<Longevity
										content={data?.longevity}
										constraints={{ noDollarPerUnitAmount: true }}
										loading={loading}
										readOnly={isReadOnlyReason}
										useSectionsErrors={useSectionsErrors}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				)}

				{!isDOMLoading && (
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("pase") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"pase"}
									title={sections.get("pase").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<PASEBonus
										sectionId={"pase"}
										reference={"PASE"}
										content={data?.pase}
										loading={loading}
										readOnly={isReadOnlyReason}
										useSectionsErrors={useSectionsErrors}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				)}
			</Grid>
		</PayPlansLayout>
	);
}
PayPlansModel.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
	isNew: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	updateModel: PropTypes.func,
};
